import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, LogOut, Trash2, Eye, QrCode } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminDashboard({ onLogout }) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [reviewsDialogOpen, setReviewsDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [qrCode, setQrCode] = useState("");
  const [reviews, setReviews] = useState([]);
  
  const [formData, setFormData] = useState({
    name: "",
    google_review_link: "",
    username: "",
    password: ""
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      toast.error("Failed to fetch businesses");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/businesses`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Business created successfully!");
      setDialogOpen(false);
      setFormData({ name: "", google_review_link: "", username: "", password: "" });
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create business");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (businessId) => {
    if (!window.confirm("Are you sure you want to delete this business?")) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Business deleted successfully");
      fetchBusinesses();
    } catch (error) {
      toast.error("Failed to delete business");
    }
  };

  const viewQRCode = async (business) => {
    setSelectedBusiness(business);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/businesses/${business.id}/qrcode`);
      setQrCode(response.data.qr_code);
      setQrDialogOpen(true);
    } catch (error) {
      toast.error("Failed to generate QR code");
    }
  };

  const viewReviews = async (business) => {
    setSelectedBusiness(business);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/${business.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
      setReviewsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch reviews");
    }
  };

  const downloadQRCode = () => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `${selectedBusiness.name}-qr-code.png`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="super-admin-dashboard">
      <div className="border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Super Admin Dashboard</h1>
          <Button onClick={onLogout} variant="outline" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Businesses</CardTitle>
                <CardDescription>Manage all business accounts</CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-business-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Business</DialogTitle>
                    <DialogDescription>Add a new business to the system</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Business Name</Label>
                      <Input
                        id="name"
                        data-testid="business-name-input"
                        placeholder="Salon ABC"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="google_link">Google Review Link</Label>
                      <Input
                        id="google_link"
                        data-testid="google-link-input"
                        placeholder="https://g.page/..."
                        value={formData.google_review_link}
                        onChange={(e) => setFormData({ ...formData, google_review_link: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        data-testid="business-username-input"
                        placeholder="salon_abc"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        data-testid="business-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="create-business-submit">
                      {loading ? "Creating..." : "Create Business"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Created At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No businesses yet. Create your first business!
                    </TableCell>
                  </TableRow>
                ) : (
                  businesses.map((business) => (
                    <TableRow key={business.id}>
                      <TableCell className="font-medium">{business.name}</TableCell>
                      <TableCell>{business.username}</TableCell>
                      <TableCell>{new Date(business.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewQRCode(business)}
                          data-testid={`view-qr-${business.id}`}
                        >
                          <QrCode className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewReviews(business)}
                          data-testid={`view-reviews-${business.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(business.id)}
                          data-testid={`delete-business-${business.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR Code - {selectedBusiness?.name}</DialogTitle>
            <DialogDescription>Customers can scan this to leave reviews</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {qrCode && <img src={qrCode} alt="QR Code" className="w-64 h-64" data-testid="qr-code-image" />}
            <Button onClick={downloadQRCode} className="w-full" data-testid="download-qr-button">
              Download QR Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reviews Dialog */}
      <Dialog open={reviewsDialogOpen} onOpenChange={setReviewsDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Reviews - {selectedBusiness?.name}</DialogTitle>
            <DialogDescription>All customer reviews and ratings</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {reviews.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No reviews yet</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm text-green-600 font-medium">High Ratings (4-5★)</div>
                    <div className="text-2xl font-bold text-green-700">
                      {reviews.filter(r => r.rating >= 4).length}
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="text-sm text-amber-600 font-medium">Medium Ratings (3★)</div>
                    <div className="text-2xl font-bold text-amber-700">
                      {reviews.filter(r => r.rating === 3).length}
                    </div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="text-sm text-red-600 font-medium">Low Ratings (1-2★)</div>
                    <div className="text-2xl font-bold text-red-700">
                      {reviews.filter(r => r.rating <= 2).length}
                    </div>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((review) => (
                      <TableRow key={review.id}>
                        <TableCell>{review.customer_name}</TableCell>
                        <TableCell>{review.customer_mobile}</TableCell>
                        <TableCell>
                          <span className={`font-semibold ${
                            review.rating >= 4 ? 'text-green-600' : 
                            review.rating === 3 ? 'text-amber-600' : 
                            'text-red-600'
                          }`}>
                            {review.rating} ★
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            review.rating >= 4 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {review.rating >= 4 ? 'Sent to Google' : 'Internal Only'}
                          </span>
                        </TableCell>
                        <TableCell>{new Date(review.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}